/**
 * ============================================
 * Fetch API with JWT Authentication
 * ============================================
 * 
 * Helper function for fetch() that automatically adds JWT token
 * 
 * Usage:
 *   import fetchWithJWT from '../api/fetchWithJWT';
 *   
 *   // Instead of:
 *   fetch(`${process.env.REACT_APP_APIURL}endpoint`)
 *   
 *   // Use:
 *   fetchWithJWT('/endpoint')
 */
import authService from '../services/authService';

const API_BASE_URL = process.env.REACT_APP_APIURL;

/**
 * Fetch with automatic JWT token injection
 * 
 * @param {string} endpoint - API endpoint (e.g., '/business/details')
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>} Fetch response
 */
const fetchWithJWT = async (endpoint, options = {}) => {
  try {
    // Get JWT token (will refresh if expired)
    const token = await authService.refreshTokenIfNeeded();

    // Build full URL - handle trailing/leading slashes properly
    let url;
    if (endpoint.startsWith('http')) {
      url = endpoint;
    } else {
      // Remove trailing slash from base URL and leading slash from endpoint to avoid double slashes
      const baseUrl = API_BASE_URL.replace(/\/$/, '');
      const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      url = `${baseUrl}${cleanEndpoint}`;
    }

    // Merge headers with JWT token
    const headers = {

        ...options.headers,
    };

    // Only set Content-Type if not uploading FormData
    // FormData needs the browser to set Content-Type automatically with boundary
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log('[FETCH] Request with JWT token:', options.method || 'GET', endpoint);
    } else {
      console.warn('[FETCH] No token available for request');
    }

    // Make fetch request
    const response = await fetch(url, {
      ...options,
      headers
    });

    // Handle 401/403 errors (token expired or invalid)
    if (response.status === 401 || response.status === 403) {
      console.warn('[FETCH] Authentication error, refreshing token...');
      
      // Try to get new token
      const newToken = await authService.initializeToken();
      
      // Retry request with new token
      headers['Authorization'] = `Bearer ${newToken}`;
      console.log('[FETCH] Retrying request with new token');
      
      return await fetch(url, {
        ...options,
        headers
      });
    }

    return response;
  } catch (error) {
    console.error('[FETCH] Error:', error);
    throw error;
  }
};

export default fetchWithJWT;
